import React, { useState } from "react";
import {
  useGetOwnPhoneNumbersQuery,
  useSubmitTollfreeVerificationMutation,
  useRefreshTollfreeVerificationMutation,
} from "@/store/rest_api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { TollFreeBadge, TOLL_FREE_TEXT_CLASS } from "@/components/phone-numbers/TollFreeBadge";
import { isTollFree, isTollfreeVerified } from "@/util/a2p";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { FaFax, FaPhone } from "react-icons/fa";
import { MoreVertical, Plus, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FaMessage, FaPhotoFilm } from "react-icons/fa6";
import PhoneNumber, { PhoneNumberCapabilities } from "@/model/interface/PhoneNumber";
import { PhoneNumberAliasCell } from "./PhoneNumberAliasCell";
import { PhoneNumberCallFlowCell } from "./PhoneNumberCallFlowCell";
import { ReleasePhoneNumberDialog } from "./ReleasePhoneNumberDialog";
import { formatMoneyAmount } from "@/util/sums";

interface MyPhoneNumbersProps {
  onSwitchToPurchase?: () => void;
}

export function MyPhoneNumbers({ onSwitchToPurchase }: MyPhoneNumbersProps) {
  const { data: phoneNumbers, isLoading } = useGetOwnPhoneNumbersQuery();
  const [submitVerification, { isLoading: isSubmittingVerification }] = useSubmitTollfreeVerificationMutation();
  const [refreshVerification, { isLoading: isRefreshingVerification }] = useRefreshTollfreeVerificationMutation();
  const [searchQuery, setSearchQuery] = useState("");
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<PhoneNumber | null>(null);

  const parseCapabilities = (capabilitiesJson: string): PhoneNumberCapabilities => {
    try {
      return JSON.parse(capabilitiesJson);
    } catch {
      return { voice: false, sms: false, mms: false, fax: false };
    }
  };

  const filteredNumbers = phoneNumbers?.filter((phone) => {
    const query = searchQuery.toLowerCase();
    return (
      phone.number.toLowerCase().includes(query) ||
      phone.alias?.toLowerCase().includes(query) ||
      phone.location.toLowerCase().includes(query)
    );
  });

  const handleVerifyTexting = async (phoneNumber: PhoneNumber) => {
    try {
      await submitVerification({ phoneNumberId: phoneNumber.id }).unwrap();
      toast.success("Verification submitted. The carrier usually reviews within a few business days.");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit the verification");
    }
  };

  const handleRefreshVerification = async (phoneNumber: PhoneNumber) => {
    try {
      await refreshVerification(phoneNumber.id).unwrap();
      toast.success("Verification status refreshed");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to refresh the verification");
    }
  };

  const tollfreeVerificationLabel = (phoneNumber: PhoneNumber) => {
    if (isTollfreeVerified(phoneNumber)) {
      return { label: "Verified for texting", variant: "default" as const };
    }
    if (phoneNumber.tollfreeVerificationStatus === "TWILIO_REJECTED") {
      return { label: "Verification rejected", variant: "destructive" as const };
    }
    if (phoneNumber.tollfreeVerificationSid) {
      return { label: "Verification in review", variant: "secondary" as const };
    }
    return { label: "Not verified for texting", variant: "outline" as const };
  };

  const handleReleaseClick = (phoneNumber: PhoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    setReleaseDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!phoneNumbers || phoneNumbers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FaPhone className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No Phone Numbers Yet</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          You haven't purchased any phone numbers yet. Get started by purchasing your first number to begin making and
          receiving calls.
        </p>
        <Button onClick={onSwitchToPurchase}>Purchase Your First Number</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Input
          type="text"
          placeholder="Search by number, alias, or location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
        {onSwitchToPurchase && (
          <Button onClick={onSwitchToPurchase} className="ml-auto">
            <Plus className="mr-2 h-4 w-4" />
            Purchase Number
          </Button>
        )}
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Alias</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Capabilities</TableHead>
              <TableHead>Monthly Cost</TableHead>
              <TableHead>Call Flow</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredNumbers && filteredNumbers.length > 0 ? (
              filteredNumbers.map((phoneNumber) => {
                const capabilities = parseCapabilities(phoneNumber.capabilities);
                return (
                  <TableRow key={phoneNumber.id}>
                    <TableCell className={isTollFree(phoneNumber) ? `font-medium ${TOLL_FREE_TEXT_CLASS}` : "font-medium"}>
                      {phoneNumber.number}
                    </TableCell>
                    <TableCell>
                      {isTollFree(phoneNumber) ? (
                        <div className="flex flex-col gap-1">
                          <TollFreeBadge className="w-fit" />
                          <Badge variant={tollfreeVerificationLabel(phoneNumber).variant} className="w-fit">
                            {tollfreeVerificationLabel(phoneNumber).label}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Local</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <PhoneNumberAliasCell phoneNumber={phoneNumber} />
                    </TableCell>
                    <TableCell>
                      {phoneNumber.location}
                      {phoneNumber.region && `, ${phoneNumber.region}`}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {capabilities.voice && (
                          <span title="Voice" className="inline-flex items-center">
                            <FaPhone className="h-4 w-4" />
                          </span>
                        )}
                        {capabilities.sms && (
                          <span title="SMS" className="inline-flex items-center">
                            <FaMessage className="h-4 w-4" />
                          </span>
                        )}
                        {capabilities.mms && (
                          <span title="MMS" className="inline-flex items-center">
                            <FaPhotoFilm className="h-4 w-4" />
                          </span>
                        )}
                        {capabilities.fax && (
                          <span title="Fax" className="inline-flex items-center">
                            <FaFax className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {phoneNumber.monthlyFee === 0
                        ? "Included"
                        : formatMoneyAmount(String(phoneNumber.monthlyFee ?? phoneNumber.monthlyCost))}
                    </TableCell>
                    <TableCell>
                      <PhoneNumberCallFlowCell phoneNumber={phoneNumber} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {isTollFree(phoneNumber) && !isTollfreeVerified(phoneNumber) && !phoneNumber.tollfreeVerificationSid && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mr-2"
                          disabled={isSubmittingVerification}
                          onClick={() => handleVerifyTexting(phoneNumber)}
                        >
                          Verify texting
                        </Button>
                      )}
                      {isTollFree(phoneNumber) && !isTollfreeVerified(phoneNumber) && phoneNumber.tollfreeVerificationSid && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mr-2"
                          disabled={isRefreshingVerification}
                          onClick={() => handleRefreshVerification(phoneNumber)}
                        >
                          Refresh status
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" title="Options">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleReleaseClick(phoneNumber)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Release number
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No phone numbers found matching your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ReleasePhoneNumberDialog
        open={releaseDialogOpen}
        onOpenChange={setReleaseDialogOpen}
        phoneNumber={selectedPhoneNumber}
      />
    </div>
  );
}
